import React, {
  useState,
  useEffect,
  useContext,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Select from "react-select";
import Loader from "../../../ui/Loader";
import { AuthContext } from "../../../../context/AuthContext";
import { fetchWithAuth } from "../../../../utils/api";
import {
  swalError,
  swalSuccess,
  swalConfirm,
  swalWarning,
} from "../../../../utils/swalAlert";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const ManagePeriods = forwardRef((props, ref) => {
  const { user } = useContext(AuthContext);
  const [periods, setPeriods] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newPeriod, setNewPeriod] = useState({
    year: "",
    enrollmentEndDate: "",
    term: "",
    status: "",
    startDate: "",
    endDate: "",
  });
  const [editPeriod, setEditPeriod] = useState({
    id: "",
    year: "",
    enrollmentEndDate: "",
    term: "",
    status: "",
    startDate: "",
    endDate: "",
  });
  const [initialEditPeriod, setInitialEditPeriod] = useState({
    id: "",
    year: "",
    enrollmentEndDate: "",
    term: "",
    status: "",
    startDate: "",
    endDate: "",
  });
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [terms, setTerms] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const columns = [
    { key: "term", label: "Term", sortable: true },
    { key: "year", label: "Year", sortable: true },
    { key: "status", label: "Status", sortable: true },
    { key: "startDate", label: "Start Date", sortable: true },
    { key: "endDate", label: "End Date", sortable: true },
    { key: "enrollmentEndDate", label: "Enrollment End Date", sortable: true },
    { key: "edit", label: "", sortable: false },
    { key: "delete", label: "", sortable: false },
  ];

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const yearOptions = useMemo(
    () => [
      { value: currentYear.toString(), label: currentYear.toString() },
      {
        value: (currentYear + 1).toString(),
        label: (currentYear + 1).toString(),
      },
      {
        value: (currentYear + 2).toString(),
        label: (currentYear + 2).toString(),
      },
    ],
    [currentYear],
  );

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setNewPeriod({
        year: currentYear.toString(),
        enrollmentEndDate: "",
        term: "",
        status: "",
        startDate: "",
        endDate: "",
      });
      setIsModalOpen(true);
    },
  }));

  const convertToUTC = (dateStr) => {
    const date = new Date(dateStr);
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() + tzOffset).toISOString();
  };

  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/period/get-all/${user.instituteId}`,
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch periods");
        const data = await response.json();
        setPeriods(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        swalError("Failed to load periods.");
      } finally {
        setLoading(false);
      }
    };

    const fetchLOVs = async () => {
      try {
        const [termsResponse, statusesResponse] = await Promise.all([
          fetchWithAuth(`${serverUrl}/api/lov/get-all-by-type/term`),
          fetchWithAuth(`${serverUrl}/api/lov/get-all-by-type/status`),
        ]);

        if (!termsResponse || !statusesResponse) return;

        if (!termsResponse.ok || !statusesResponse.ok) {
          throw new Error("Failed to fetch LOVs");
        }

        const termsData = await termsResponse.json();
        const statusesData = await statusesResponse.json();

        const termsOptions = termsData.map((term) => ({
          value: term.value,
          label: term.value,
        }));
        const statusesOptions = statusesData.map((status) => ({
          value: status.value,
          label: status.value,
        }));

        setTerms(termsOptions);
        setStatuses(statusesOptions);
      } catch (err) {
        swalError("Failed to load LOVs.");
      }
    };

    fetchPeriods();
    fetchLOVs();
  }, [user.instituteId]);

  const handleDelete = async (periodId) => {
    const result = await swalConfirm(
      "Are you sure?",
      "You won't be able to revert this!",
      "Yes, delete it!",
      "Cancel",
    );

    if (!result.isConfirmed) return;

    setIsDeleting(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/period/delete/${periodId}`,
        {
          method: "DELETE",
        },
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete period");
      }
      setPeriods(periods.filter((period) => period._id !== periodId));
      swalSuccess("Period deleted successfully");
    } catch (err) {
      swalError("Failed to delete period.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreatePeriod = async (e) => {
    e.preventDefault();
    if (
      !newPeriod.year.trim() ||
      !newPeriod.term ||
      !newPeriod.status ||
      !newPeriod.startDate ||
      !newPeriod.endDate
    ) {
      swalError("Please provide all required fields.");
      return;
    }

    const startDate = new Date(newPeriod.startDate);
    const endDate = new Date(newPeriod.endDate);
    if (endDate <= startDate) {
      swalError("End date must be after Start date.");
      return;
    }

    if (newPeriod.enrollmentEndDate) {
      const enrollmentEndDate = new Date(newPeriod.enrollmentEndDate);
      if (enrollmentEndDate > endDate) {
        swalError("Enrollment End Date must be on or before End Date.");
        return;
      }
    }

    setIsCreating(true);
    try {
      const response = await fetchWithAuth(`${serverUrl}/api/period/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          year: newPeriod.year,
          enrollmentEndDate: newPeriod.enrollmentEndDate
            ? convertToUTC(newPeriod.enrollmentEndDate)
            : null,
          term: newPeriod.term,
          status: newPeriod.status,
          startDate: convertToUTC(newPeriod.startDate),
          endDate: convertToUTC(newPeriod.endDate),
          instituteId: user.instituteId,
        }),
      });
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create period");
      }
      const createdPeriod = await response.json();
      setPeriods([...periods, createdPeriod]);
      setIsModalOpen(false);
      setNewPeriod({
        year: currentYear.toString(),
        enrollmentEndDate: "",
        term: "",
        status: "",
        startDate: "",
        endDate: "",
      });
      swalSuccess("Period created successfully");
    } catch (err) {
      swalError(err.message || "Failed to create period.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditPeriod = async (e) => {
    e.preventDefault();
    if (
      !editPeriod.year.trim() ||
      !editPeriod.term ||
      !editPeriod.status ||
      !editPeriod.startDate ||
      !editPeriod.endDate
    ) {
      swalError("Please provide all required fields.");
      return;
    }

    const startDate = new Date(editPeriod.startDate);
    const endDate = new Date(editPeriod.endDate);
    if (endDate <= startDate) {
      swalError("End date must be after Start date.");
      return;
    }

    if (editPeriod.enrollmentEndDate) {
      const enrollmentEndDate = new Date(editPeriod.enrollmentEndDate);
      if (enrollmentEndDate > endDate) {
        swalError("Enrollment End Date must be on or before End Date.");
        return;
      }
    }

    setIsUpdating(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/period/update/${editPeriod.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            year: editPeriod.year,
            enrollmentEndDate: editPeriod.enrollmentEndDate
              ? convertToUTC(editPeriod.enrollmentEndDate)
              : null,
            term: editPeriod.term,
            status: editPeriod.status,
            startDate: convertToUTC(editPeriod.startDate),
            endDate: convertToUTC(editPeriod.endDate),
            instituteId: user.instituteId,
          }),
        },
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update period");
      }
      const updatedPeriod = await response.json();
      setPeriods(
        periods.map((period) =>
          period._id === updatedPeriod._id ? updatedPeriod : period,
        ),
      );
      setIsEditModalOpen(false);
      setEditPeriod({
        id: "",
        year: "",
        enrollmentEndDate: "",
        term: "",
        status: "",
        startDate: "",
        endDate: "",
      });
      setInitialEditPeriod({
        id: "",
        year: "",
        enrollmentEndDate: "",
        term: "",
        status: "",
        startDate: "",
        endDate: "",
      });
      setIsEditChanged(false);
      swalSuccess("Period updated successfully");
    } catch (err) {
      swalError("Failed to update period.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditSelectChange = (selectedOption, { name }) => {
    setEditPeriod((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleEditDateChange = (e) => {
    const { name, value } = e.target;
    setEditPeriod((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const fieldsToCheck = [
      "year",
      "term",
      "status",
      "startDate",
      "endDate",
      "enrollmentEndDate",
    ];
    const hasChanged = fieldsToCheck.some(
      (field) => editPeriod[field] !== initialEditPeriod[field],
    );
    setIsEditChanged(hasChanged);
  }, [editPeriod, initialEditPeriod]);

  const enhancedPeriods = periods.map((period) => {
    const userDate = (dateStr) => {
      const date = new Date(dateStr);
      const tzOffset = date.getTimezoneOffset() * 60000;
      if (tzOffset > 0) {
        return new Date(date.getTime() + tzOffset).toLocaleDateString();
      } else {
        return new Date(date.getTime() - tzOffset).toLocaleDateString();
      }
    };

    return {
      ...period,
      year: period.year,
      term: period.term,
      status: period.status,
      startDate: period.startDate ? userDate(period.startDate) : "",
      endDate: period.endDate ? userDate(period.endDate) : "",
      enrollmentEndDate: period.enrollmentEndDate
        ? userDate(period.enrollmentEndDate)
        : "",
      edit: (
        <button
          className="edit-btn"
          onClick={() => {
            setEditPeriod({
              id: period._id,
              year: period.year.toString(),
              enrollmentEndDate: period.enrollmentEndDate
                ? period.enrollmentEndDate.slice(0, 10)
                : "",
              term: period.term,
              status: period.status,
              startDate: period.startDate ? period.startDate.slice(0, 10) : "",
              endDate: period.endDate ? period.endDate.slice(0, 10) : "",
            });
            setInitialEditPeriod({
              id: period._id,
              year: period.year.toString(),
              enrollmentEndDate: period.enrollmentEndDate
                ? period.enrollmentEndDate.slice(0, 10)
                : "",
              term: period.term,
              status: period.status,
              startDate: period.startDate ? period.startDate.slice(0, 10) : "",
              endDate: period.endDate ? period.endDate.slice(0, 10) : "",
            });
            setIsEditModalOpen(true);
            setIsEditChanged(false);
          }}
          disabled={isDeleting || isUpdating || isCreating}
        >
          <i className="fa-solid fa-pen-to-square"></i>
        </button>
      ),
      delete: (
        <button
          className="delete-btn"
          onClick={() => handleDelete(period._id)}
          disabled={isDeleting || isUpdating || isCreating}
        >
          <i className="fa-solid fa-trash-can"></i>
        </button>
      ),
    };
  });

  return (
    <>
      <div className="manage-container">
        <div className="header">
          <button className="create-btn" onClick={() => setIsModalOpen(true)}>
            Add Period +
          </button>
        </div>
      </div>
      <div className="manage-container">
        {loading && <Loader fontSize="2rem" />}
        {error && <div className="error-message">{error}</div>}
        {!loading && !error && (
          <Table data={enhancedPeriods} columns={columns} caption="Periods" />
        )}
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => !isCreating && setIsModalOpen(false)}
      >
        <h2>Add New Period</h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Fill in the details below to add a new period to your institute.
        </p>
        <form onSubmit={handleCreatePeriod} className="period-form">
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Year
            </label>
            <Select
              name="year"
              options={yearOptions}
              value={yearOptions.find(
                (option) => option.value === newPeriod.year,
              )}
              onChange={(selectedOption) =>
                setNewPeriod({
                  ...newPeriod,
                  year: selectedOption ? selectedOption.value : "",
                })
              }
              placeholder="Select year..."
              isClearable={false}
              required
              isDisabled={isCreating}
            />
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Term
            </label>
            <Select
              name="term"
              options={terms}
              value={terms.find((option) => option.value === newPeriod.term)}
              onChange={(selectedOption) =>
                setNewPeriod({
                  ...newPeriod,
                  term: selectedOption ? selectedOption.value : "",
                })
              }
              placeholder="Select term..."
              isClearable
              required
              isDisabled={isCreating}
            />
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Status
            </label>
            <Select
              name="status"
              options={statuses}
              value={statuses.find(
                (option) => option.value === newPeriod.status,
              )}
              onChange={(selectedOption) =>
                setNewPeriod({
                  ...newPeriod,
                  status: selectedOption ? selectedOption.value : "",
                })
              }
              placeholder="Select status..."
              isClearable
              required
              isDisabled={isCreating}
            />
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Start Date
            </label>
            <input
              type="date"
              value={newPeriod.startDate}
              onChange={(e) =>
                setNewPeriod({ ...newPeriod, startDate: e.target.value })
              }
              required
              disabled={isCreating}
            />
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> End Date
            </label>
            <input
              type="date"
              value={newPeriod.endDate}
              onChange={(e) =>
                setNewPeriod({ ...newPeriod, endDate: e.target.value })
              }
              required
              min={newPeriod.startDate}
              disabled={isCreating}
            />
          </div>
          <div className="form-group">
            <label>
              Enrollment End Date{" "}
              <span className="optional-text">(Optional)</span>
            </label>
            <input
              type="date"
              value={newPeriod.enrollmentEndDate}
              onChange={(e) =>
                setNewPeriod({
                  ...newPeriod,
                  enrollmentEndDate: e.target.value,
                })
              }
              max={newPeriod.endDate}
              disabled={isCreating}
            />
            <small className="helper-text">
              This is the last date students can enroll (optional).
            </small>
          </div>
          {isCreating ? (
            <Loader />
          ) : (
            <button type="submit" className="btn-submit">
              Create Period
            </button>
          )}
        </form>
      </Modal>
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => !isUpdating && setIsEditModalOpen(false)}
      >
        <h2>Edit Period</h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Update the details below to edit the period information.
        </p>
        <form onSubmit={handleEditPeriod} className="period-form">
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Year
            </label>
            <Select
              name="year"
              options={yearOptions}
              value={yearOptions.find(
                (option) => option.value === editPeriod.year,
              )}
              onChange={handleEditSelectChange}
              placeholder="Select year..."
              isClearable={false}
              required
              isDisabled={isUpdating}
            />
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Term
            </label>
            <Select
              name="term"
              options={terms}
              value={terms.find((option) => option.value === editPeriod.term)}
              onChange={handleEditSelectChange}
              placeholder="Select term..."
              isClearable
              required
              isDisabled={isUpdating}
            />
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Status
            </label>
            <Select
              name="status"
              options={statuses}
              value={statuses.find(
                (option) => option.value === editPeriod.status,
              )}
              onChange={handleEditSelectChange}
              placeholder="Select status..."
              isClearable
              required
              isDisabled={isUpdating}
            />
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={editPeriod.startDate}
              onChange={handleEditDateChange}
              required
              disabled={isUpdating}
            />
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={editPeriod.endDate}
              onChange={handleEditDateChange}
              required
              min={editPeriod.startDate}
              disabled={isUpdating}
            />
          </div>
          <div className="form-group">
            <label>
              Enrollment End Date{" "}
              <span className="optional-text">(Optional)</span>
            </label>
            <input
              type="date"
              name="enrollmentEndDate"
              value={editPeriod.enrollmentEndDate}
              onChange={handleEditDateChange}
              max={editPeriod.endDate}
              disabled={isUpdating}
            />
            <small className="helper-text">
              This is the last date students can enroll (optional).
            </small>
          </div>
          {isUpdating ? (
            <Loader />
          ) : (
            <button
              type="submit"
              className="btn-submit"
              disabled={!isEditChanged || isUpdating}
              onClick={(e) => {
                if (!isEditChanged && !isUpdating) {
                  e.preventDefault();
                  swalWarning("No changes detected.");
                }
              }}
            >
              Update Period
            </button>
          )}
        </form>
      </Modal>
    </>
  );
});

export default ManagePeriods;
